// packages/ui/src/hooks/useAuth.ts
//
// Zustand store + `useAuth()` hook — canonical FE auth state per Task 42 +
// CONTEXT. Wraps the openapi-fetch `apiClient` from
// `@printway/shared` so every call is typed by the live OpenAPI schema and
// rides the HttpOnly JWT + XSRF cookie pair.
//
// Store shape (matches Task 42 brief verbatim):
//   user, permissions, permVersion, isLoading,
//   login(creds) → { requires2FA?, method? }
//   logout()
//   refresh()
//   fetchMe() — GET /auth/me + /auth/me/permissions
//
// Notes:
//   - `AuthUser` is intentionally a slim FE-side mirror of the `/auth/me`
//     wire shape (id, email, displayName, primaryRole, status, etc.). The
//     real types come from `paths['/auth/me']['get']['responses']['200']`
//     once `pnpm api:sync` regenerates `types.generated.ts`. Until then
//     the FE compiles against this hand-written interface so screens can
//     render `user.displayName` without `any`.
//   - All HTTP calls go through `apiClient()` (no raw fetch) — this keeps
//     XSRF + cookie semantics consistent with mutating verbs.
//   - The store is configured with `subscribeWithSelector` so cross-tab
//     BroadcastChannel logout (Task 43) can subscribe to slices.

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import { apiClient } from '@printway/shared/api';

// ---------------------------------------------------------------------------
// FE-side AuthUser type (slim mirror of /auth/me response).
// Kept in lock-step with `apps/api/src/modules/auth/routes/me.routes.ts`.
// ---------------------------------------------------------------------------

// vg-binding: P18.D-01
/**
 * Vendor membership role — mirrors `VENDOR_MEMBERSHIP_ROLES` in
 * `apps/api/src/modules/auth/models/VendorMembership.ts` (Tier 2b, 6 values:
 * `owner` + 5 staff roles).
 *
 * P18.D-01: this is the REAL permission axis for the vendor portal — `owner`
 * manages API keys / webhook URL, the 5 staff roles are read-only on the
 * integration surface. It is deliberately NOT a binary `owner|staff` flag: the
 * role string is surfaced verbatim so callers can render the actual role and
 * so a future per-role grant does not need another wire change.
 */
export type VendorMembershipRole =
  | 'owner'
  | 'operator'
  | 'qc'
  | 'packaging'
  | 'finance'
  | 'viewer';

/**
 * One entry of `/auth/me` → `vendorMemberships[]` (`me.routes.ts:339`).
 *
 * Only ACTIVE memberships are returned (the route filters
 * `status: 'active'`), so a revoked/removed membership simply disappears from
 * the array — which is what makes P18.D-02 ("owner removed from vendor → loses
 * key management immediately") hold on the FE without a second call.
 */
export interface VendorMembershipSummary {
  vendorId: string;
  role: VendorMembershipRole;
}

/** Wire status returned by `/auth/me` (translation done server-side). */
export type AuthUserStatus =
  | 'Active'
  | 'Unverified'
  | 'Suspended'
  | 'Locked'
  | 'PendingApproval'
  | 'Inactive';

/**
 * Minimal user shape consumed by FE. Mirrors `/auth/me` Block 2 output —
 * extend cautiously; large PII surfaces should live behind dedicated
 * profile endpoints, not the home auth bootstrap.
 */
export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  primaryRole: string;
  activeRoleSlot: string;
  status: AuthUserStatus;
  language: string | null;
  themePreference: 'light' | 'dark' | 'system' | 'auto' | null;
  /** Optional — only present on impersonation sessions. */
  actualUserId?: string;
  // portal scope identifiers — used by per-SPA <RoleGuard>
  // to detect wrong-portal access. Null when user has no membership.
  merchantId?: string | null;
  vendorId?: string | null;
  /**
   * P18.D-01 — active vendor memberships from `/auth/me` (`me.routes.ts:339`
   * already returned this; the FE mirror was just dropping it). Optional so
   * adding it stays blast-safe for the admin/merchant portals, which never
   * read it.
   */
  vendorMemberships?: VendorMembershipSummary[];
  /** Additive role grants (e.g., admin user granted 'support_admin'). */
  roles?: string[];
  /** P8.D-508 — pending mandatory first-login 2FA enrollment. Session is
   *  restricted server-side to the 2FA-setup allowlist until enrollment
   *  completes; guards route to the Setup 2FA screen when true. */
  mustSetup2FA?: boolean;
  // Profile fields (UAT-1 fix 2026-05-16): /auth/me returns these so the
  // Profile form can hydrate without losing saved values on every re-fetch.
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  timezone?: string | null;
  // R3-10 (2026-05-16): billing block surfaced on /auth/me so the Billing
  // settings page can hydrate existing values without a separate GET.
  billingInfo?: {
    legalName?: string | null;
    taxId?: string | null;
    billingEmail?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
  } | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResult {
  requires2FA?: boolean;
  method?: string;
}

// ---------------------------------------------------------------------------
// Store state
// ---------------------------------------------------------------------------

export interface AuthState {
  user: AuthUser | null;
  permissions: string[];
  permVersion: number;
  isLoading: boolean;
  /** Last error code surfaced from a login/logout/refresh/fetchMe call. */
  lastError: string | null;

  login: (creds: LoginCredentials) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  fetchMe: () => Promise<void>;
  /** Imperative reset — used by BroadcastChannel logout listener (Task 43). */
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Helpers — narrow envelope unwrappers
// ---------------------------------------------------------------------------

interface SuccessEnvelope<T> {
  success: true;
  data: T;
  requestId: string;
}
interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
  requestId: string;
}
type Envelope<T> = SuccessEnvelope<T> | ErrorEnvelope;

function unwrap<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as Envelope<T>).success === true &&
    'data' in payload
  ) {
    return (payload as SuccessEnvelope<T>).data;
  }
  // Tolerate raw shapes (e.g. when codegen pre-bind is empty).
  return payload as T;
}

function envelopeError(payload: unknown): string | null {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as Envelope<unknown>).success === false &&
    'error' in payload
  ) {
    return (payload as ErrorEnvelope).error.code ?? null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

// Dedup concurrent /auth/me + /auth/me/permissions round-trips. Multiple
// components (AuthGuard, sidebar, App bootstrap) call fetchMe() on mount and
// React StrictMode double-invokes effects in dev, so the pair fired several
// times per F5. Share one in-flight promise; cleared on settle so a later
// (sequential) refresh still refetches.
let inFlightFetchMe: Promise<void> | null = null;

export const useAuth = create<AuthState>()(
  subscribeWithSelector((set, get) => ({
    user: null,
    permissions: [],
    permVersion: 0,
    isLoading: false,
    lastError: null,

    async login(creds) {
      set({ isLoading: true, lastError: null });
      try {
        const client = apiClient();
        // openapi-fetch — the typed POST signature kicks in once
        // `paths` is populated by `pnpm api:sync`. Until then we cast
        // the path string and body to `any` so the stub typings don't
        // block compilation; behaviour at runtime is unchanged.
        const { data, error, response } = await (
          client as unknown as {
            POST: (
              path: string,
              init: { body: unknown },
            ) => Promise<{
              data?: unknown;
              error?: unknown;
              response: Response;
            }>;
          }
        ).POST('/api/v1/auth/login', { body: creds });

        if (error || !response.ok) {
          const code = envelopeError(error) ?? `HTTP_${response.status}`;
          set({ isLoading: false, lastError: code });
          throw new Error(code);
        }

        const payload = unwrap<{
          requires2FA?: boolean;
          method?: string;
          user?: AuthUser;
          permissions?: string[];
          permVersion?: number;
        }>(data);

        // Two paths..:
        //   1. 2FA gate — server replies { requires2FA, method }; user
        //      stays unauthenticated; FE redirects to OTP screen.
        //   2. Direct success — server sets cookies; we hydrate state.
        if (payload.requires2FA) {
          set({ isLoading: false });
          const result: LoginResult = { requires2FA: true };
          if (payload.method !== undefined) result.method = payload.method;
          return result;
        }

        if (payload.user) {
          set({
            user: payload.user,
            permissions: payload.permissions ?? [],
            permVersion: payload.permVersion ?? 0,
            isLoading: false,
          });
        } else {
          // Fallback — fetch /me + /me/permissions if login response is slim.
          set({ isLoading: false });
          await get().fetchMe();
        }
        return {};
      } catch (err) {
        const code = err instanceof Error ? err.message : 'LOGIN_FAILED';
        set({ isLoading: false, lastError: code });
        throw err;
      }
    },

    async logout() {
      set({ isLoading: true });
      try {
        const client = apiClient();
        await (
          client as unknown as {
            POST: (
              path: string,
              init?: { body?: unknown },
            ) => Promise<{ response: Response }>;
          }
        ).POST('/api/v1/auth/logout');
      } catch {
        // Even if the network call fails we drop local state — cookies
        // will be invalidated server-side by JWT exp anyway.
      } finally {
        get().reset();
      }
    },

    async refresh() {
      const client = apiClient();
      try {
        const { response } = await (
          client as unknown as {
            POST: (
              path: string,
              init?: { body?: unknown },
            ) => Promise<{ response: Response }>;
          }
        ).POST('/api/v1/auth/refresh');
        if (!response.ok) {
          set({ lastError: `HTTP_${response.status}` });
          return;
        }
        // Refresh succeeded — re-fetch /me to capture any permVersion bump
        // (cache invalidation flow).
        await get().fetchMe();
      } catch (err) {
        set({
          lastError: err instanceof Error ? err.message : 'REFRESH_FAILED',
        });
      }
    },

    async fetchMe() {
      if (inFlightFetchMe) return inFlightFetchMe;
      inFlightFetchMe = (async () => {
      set({ isLoading: true, lastError: null });
      try {
        const client = apiClient();
        const meCall = (
          client as unknown as {
            GET: (
              path: string,
            ) => Promise<{
              data?: unknown;
              error?: unknown;
              response: Response;
            }>;
          }
        ).GET('/api/v1/auth/me');
        const permsCall = (
          client as unknown as {
            GET: (
              path: string,
            ) => Promise<{
              data?: unknown;
              error?: unknown;
              response: Response;
            }>;
          }
        ).GET('/api/v1/auth/me/permissions');

        const [meRes, permsRes] = await Promise.all([meCall, permsCall]);

        if (!meRes.response.ok) {
          set({
            user: null,
            permissions: [],
            permVersion: 0,
            isLoading: false,
            lastError: `HTTP_${meRes.response.status}`,
          });
          return;
        }

        const meData = unwrap<AuthUser>(meRes.data);

        // R2-01: a failed permissions call used to fall through to the success
        // branch below with `permissions=[]` and `lastError=null` — every gated
        // control rendered disabled, indistinguishable from a view-only account,
        // and nothing told the operator why. Keep the user (the session is
        // valid) but record the failure so consumers can surface it.
        if (!permsRes.response.ok) {
          set({
            user: meData,
            permissions: [],
            permVersion: 0,
            isLoading: false,
            lastError: `PERMS_HTTP_${permsRes.response.status}`,
          });
          return;
        }

        const permsData = unwrap<{
          permissions: string[];
          permsVersion?: number;
          permVersion?: number;
        }>(permsRes.data);

        set({
          user: meData,
          permissions: Array.isArray(permsData?.permissions)
            ? permsData.permissions
            : [],
          permVersion:
            permsData?.permsVersion ?? permsData?.permVersion ?? 0,
          isLoading: false,
        });
      } catch (err) {
        set({
          user: null,
          permissions: [],
          permVersion: 0,
          isLoading: false,
          lastError: err instanceof Error ? err.message : 'FETCH_ME_FAILED',
        });
      }
      })();
      try {
        return await inFlightFetchMe;
      } finally {
        inFlightFetchMe = null;
      }
    },

    reset() {
      set({
        user: null,
        permissions: [],
        permVersion: 0,
        isLoading: false,
        lastError: null,
      });
    },
  })),
);
